import { Allotment } from "allotment";
import { createContext, lazy, memo, Suspense, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { Spinner } from "react-bootstrap";
import { ResizeAwareDiv, useResizeContext } from "./ResizeablePanel";
import PropTypes from 'prop-types';
import { DropTarget } from "./DragDropComponents";
import { DesktopContext, getValidLayouts, getWidth, layoutInvalid, PanelContext, useDesktop } from "./DesktopContext";
import { deepEqual } from "vega-lite";
import OfflineMask from "./OfflineMask";
import { useSubscribeAll } from "../hooks/broadcastHook";
import { useSessionState } from "../hooks/sessionStateHook";
import { useSettingState } from "../hooks/settingStorageHook";

export const PlaceholderPanel = () => <div>Drop control here</div>
export class DesktopComponentRegistry {
    #lists = [];
    #components = new Map([
        ['placeholderPanel', { component: PlaceholderPanel }],
        ['toolbar', { component: lazy(() => import('./DesktopToolbar')), offline: true }],
        ['customFormPanel', { component: lazy(() => import('./CustomFormPanel')) }]
    ]);

    lists() {
        return [...this.#lists];
    }

    components() {
        return Array.from(this.#components.values());
    }

    addList(list) {
        return this.#lists.push(list);
    }

    getList(id) {
        return this.#lists[id - 1];
    }

    getComponent(id) {
        return this.#components.get(id);
    }

    register(entry) {
        this.#components.set(entry.sid, entry)
    }
}

const initialComponent = (registry, components) => {
    const entry = components.find(c => typeof c === 'string' ? registry.getComponent(c) : registry.getComponent(c.id));
    if (entry) {
        return typeof entry === 'string' ? { id: entry } : entry;
    } else {
        return typeof components[0] === 'string' ? { id: components[0] } : components[0];
    }
}

const getLayout = (width, layouts, delta = 0) => {
    const layoutWidth = getWidth(width, delta);
    const validLayouts = getValidLayouts(width, layouts, delta);
    switch (layoutWidth) {
        case undefined:
            return undefined;
        case 'wide':
            return (validLayouts.find(l => l.default) || validLayouts.find(l => l.defaultWide) || validLayouts.find(l => l.defaultMedium) || validLayouts.find(l => l.defaultNarrow) || validLayouts[0])?.id;
        case 'medium':
            return (validLayouts.find(l => l.default) || validLayouts.find(l => l.defaultMedium) || validLayouts.find(l => l.defaultNarrow) || validLayouts[0])?.id;
        case 'narrow':
            return (validLayouts.find(l => l.default) || validLayouts.find(l => l.defualtNarrow) || validLayouts[0])?.id;
        default:
            throw new Error('Unknown width ' + layoutWidth);
    }
}

const EMPTY_OBJECT = {};

const PanelStateContext = createContext({});
const usePanelState = (panelId) => {
    const panelStates = useContext(PanelStateContext);
    return panelStates[panelId] || {
        componentID: 'placeholderPanel',
        args: {},
        title: undefined,
        hideTitle: false,
        style: undefined
    }
}

const Panel = memo(({ id, fixed, className = '' }) => {
    const { registry, setDisplayProperties, disableOfflineMask, setPanelState, setComponent } = useDesktop();
    const { componentID, args, title, hideTitle, style } = usePanelState(id);
    const context = useMemo(() => ({
        id,
        title,
        hideTitle: !!hideTitle,
        setDisplayProperties: (props) => {
            // Extract title, hideTitle, and style to panel state
            const { title, hideTitle, args, ...displayProps } = props;
            
            setPanelState(id, prev => ({
                ...prev,
                ...(title !== undefined && { title }),
                ...(hideTitle !== undefined && { hideTitle }),
                ...(args !== undefined && { args })
            }));
            
            setDisplayProperties(id, displayProps);
        }
    }), [id, setDisplayProperties, setPanelState, hideTitle, title]);

    const handleDrop = useCallback(data => {
        setComponent(data, id)
    }, [id, setComponent]);

    const entry = useMemo(() => registry.getComponent(componentID), [componentID, registry]);
    
    const Clazz = entry?.component ?? PlaceholderPanel;

    return <PanelContext.Provider value={context}>
        <DropTarget kind="component" version={1} onDrop={(fixed || entry?.fixed) ? null : handleDrop}>
            <ResizeAwareDiv id={componentID} data-component-id={componentID} className={`h-100 ${className}`} style={{ overflowY: 'auto', overflowX: 'hidden', ...style }}>
                <Suspense fallback={<Spinner></Spinner>}>
                    <Clazz {...args}></Clazz>
                    {!disableOfflineMask && <OfflineMask supportsOffline={!!entry?.offline}></OfflineMask>}
                </Suspense>
            </ResizeAwareDiv>
        </DropTarget>
    </PanelContext.Provider>
})

/**
 * A multi-panel component layout with a taskbar to allow components to be rearranged.
 * @component
 */
export const Desktop = ({ id, layouts, components, handlers, onLayoutChange, hideLayouts, onPopout, disableOfflineMask = false, children }) => {
    const { width: screenWidth } = useResizeContext();
    const initialLayout = getLayout(screenWidth, layouts);
    const [persistLayout, setPersistLayout] = useSessionState('desktopLayoutPersist', true);
    const [layoutID, setLayoutID] = useSettingState(`${id}_desktop_layoutID`, initialLayout, { followServer: false, persistChanges: persistLayout });
    const buildStateAndPropsForLayout = useCallback((id) => {
        const layout = layouts.find(l => l.id === id) || { columns: [] };
        const state = {};
        for (const col of layout.columns) {
            for (const row of col.rows) {
                const initial = initialComponent(components, row.components);
                state[row.id] = {
                    componentID: initial.id,
                    components: row.components,
                    title: row.title,
                    hideTitle: row.hideTitle || false,
                    args: initial.args
                };
            }
        }
        const props = {};
        return { state, props };
    }, [layouts, components]);
    const { state: initialState, props: initialProps } = useMemo(() => buildStateAndPropsForLayout(initialLayout), [initialLayout, buildStateAndPropsForLayout]);
    const [panelProperties, setPanelProperties] = useSettingState(`${id}_desktop_panelProperties`, initialProps, { followServer: false, persistChanges: persistLayout });
    const [panelStates, setPanelStates] = useSettingState(`${id}_desktop_panelStates`, initialState, { followServer: false, persistChanges: persistLayout });
    
    const layout = useMemo(() => layouts.find(l => l.id === layoutID) || { columns: [] }, [layouts, layoutID]);
    const prevWidth = useRef();

    useEffect(() => {
        onLayoutChange?.(layoutID);
    }, [onLayoutChange, layoutID]);
    
    const changeLayout = useCallback((id, force = true) => {
        setLayoutID(prev => {
            if (prev !== id || force) {
                const { state, props } = buildStateAndPropsForLayout(id);
                setPanelStates(state);
                setPanelProperties(props);
            }
            return id;
        });
    }, [setLayoutID, buildStateAndPropsForLayout, setPanelStates, setPanelProperties]);

    useEffect(() => {
        if (!prevWidth.current || screenWidth !== prevWidth.current) {
            const activeLayout = layouts.find(l => l.id === layoutID);
            const currLayout = getLayout(screenWidth, layouts);
            if (!activeLayout || layoutInvalid(screenWidth, activeLayout)) {
                changeLayout(currLayout);
                prevWidth.current = screenWidth;
                return;
            }
            if (prevWidth.current) {
                const prevLayout = getLayout(prevWidth.current, layouts);
                if (prevLayout !== currLayout && currLayout !== layoutID) {
                    changeLayout(currLayout);
                }
            }
            prevWidth.current = screenWidth;
        }
    }, [screenWidth, layoutID, changeLayout, layouts]);

    const defaultSizes = useMemo(() => layout.columns.map(c => c.proportion), [layout]);

    const setPanelState = useCallback((panelId, updates) => {
        setPanelStates(prev => {
            const newState = typeof updates === 'function' ? updates(prev[panelId]) : updates;
            return {
                ...prev,
                [panelId]: newState
            };
        });
    }, [setPanelStates]);

    const setComponent = useCallback(({ component: newComponentID, args: newArgs }, id) => {
        setPanelStates(panelState => {
            if (!id) {
                if (Object.values(panelState).some(panel => panel.componentID === newComponentID)) {
                    return panelState;
                }
                const target = Object.entries(panelState).reduce((target, [id, panel]) => {
                    if (panel?.components?.includes(newComponentID)) {
                        return id;
                    }
                    return target;
                }, null);
                id = target ?? 'main'
            }
            if (panelState[id]?.fixed) {
                return panelState;
            }
            const component = components.getComponent(newComponentID);
            if (component) {
                return { ...panelState, [id]: { ...panelState[id], componentID: newComponentID, args: { ...component.args, ...newArgs }} }
            }
            return panelState
        });
    }, [components, setPanelStates]);

    const setDisplayProperties = useCallback((id, properties) => {
        setPanelProperties(props => {
            // because components set these properties they may be repeatedly set
            // we need to make sure not to cause an infinite render loop so only change the object
            // identity if the values have changed
            if (!deepEqual(props?.[id], properties)) {
                return { ...props, [id]: { ...props?.[id], ...properties } };
            }
            return props;
        });
    }, [setPanelProperties]);

    const swapComponent = useCallback((currentComponent, { component: newComponentID, args: newArgs }) => {
        setPanelStates(panelState => {
            for (const [id, panel] of Object.entries(panelState)) {
                if (panel.componentID === currentComponent) {
                    const component = components.getComponent(newComponentID);
                    return { ...panelState, [id]: { ...panel, componentID: newComponentID, args: { ...component.args, ...newArgs } }}
                }
            }
            return panelState;
        });
    }, [components, setPanelStates]);

    const handleEvent = useCallback((event, data) => {
        let target = handlers?.[event];
        if (typeof target === 'function') {
            target = target(data);
        }
        if (target?.layout) {
            changeLayout(target.layout, false);
        }
        if (target?.component) {
            setComponent({ component: target.component, args: data });
        } else if (target?.swap) {
            swapComponent(target.swap[0], { component: target.swap[1], args: data });
        }
    }, [handlers, changeLayout, setComponent, swapComponent]);

    useSubscribeAll(handleEvent)

    const context = useMemo(() => ({ registry: components, layoutID, changeLayout, layouts, onPopout, screenWidth, setPanelState, disableOfflineMask, setComponent, swapComponent, setDisplayProperties, persistLayout, setPersistLayout }), [components, layoutID, changeLayout, layouts, onPopout, screenWidth, disableOfflineMask, setComponent, swapComponent, setDisplayProperties, setPanelState, persistLayout, setPersistLayout]);

    return <div className="w-100 h-100">
        <DesktopContext.Provider value={context}>
            <PanelStateContext.Provider value={panelStates}>
                <Allotment key={layoutID} className="w-100 h-100" snap={true} defaultSizes={defaultSizes}>
                    {layout.columns.map(col => {
                        return <Allotment.Pane key={layoutID + '_' + col.id} minSize={300} preferredSize={col.proportion + '%'}>
                            <Allotment className="w-100 h-100" snap={true} vertical={true} defaultSizes={col.rows.map(row => row.proportion)}>
                                {col.rows.map(row => {
                                        const { 
                                            maxSize, 
                                            minSize, 
                                            preferredSize,
                                            ...props 
                                        } = panelProperties?.[row.id] ?? EMPTY_OBJECT;
                                        
                                        return (
                                            <Allotment.Pane 
                                                key={`layout${layoutID}_${col.id}_${row.id}${minSize}${maxSize}`} 
                                                className="w-100" 
                                                minSize={minSize ?? row.minSize} 
                                                maxSize={maxSize ?? row.maxSize}
                                                preferredSize={preferredSize ?? row.preferredSize} 
                                                {...props}
                                            >
                                                <Panel 
                                                    id={row.id} 
                                                    components={row.components} 
                                                    fixed={row.fixed}
                                                />
                                            </Allotment.Pane>
                                        );
                                    })}
                            </Allotment>
                        </Allotment.Pane>
                    })}
                </Allotment>
            </PanelStateContext.Provider>
            {children}
        </DesktopContext.Provider>
    </div>
}

Desktop.propTypes = {
    /**
     * A list of components which may be placed on the desktop either by default or by placement
     */
    components: PropTypes.object.isRequired,
    /**
     * A list of panel configurations that can be used with this desktop
     */
    layouts: PropTypes.array.isRequired,
    /**
     * A function to call when the layout of the desktop is changed (either the components visible
     * are chaned or the layout of the components themselves.)
     */
    onLayoutChange: PropTypes.func,
    /**
     * Function to call to pop a component out into a new window - undefined means popout is not supported
     */
    onPopout: PropTypes.func,
    /**
     * Any nodes which interact with the desktop to provide default and responsive behaviours
     */
    children: PropTypes.node
}